"use client";

import { createContext, createElement, ReactNode, useContext } from "react";
import { useRouter } from "next/compat/router";

/**
 * The router values shared components read, without depending on one router.
 * The Pages Router provides them through `next/router`; an App Router app
 * renders a `RouterInfoProvider` instead (there is no `next/router` there).
 */
export type RouterInfo = {
  /** Locale (site key), e.g. "de-DE" */
  locale: string;
  /** Route of the page, e.g. "/procedure" (Pages Router `router.route`) */
  route: string;
  /** Same as `route` for static routes (Pages Router `router.pathname`) */
  pathname: string;
  /** Path as shown in the browser, e.g. "/verfahren" (Pages Router `router.asPath`) */
  asPath: string;
  push: (url: string) => void;
};

const RouterInfoContext = createContext<RouterInfo | null>(null);

export const RouterInfoProvider = ({
  value,
  children,
}: {
  value: RouterInfo;
  children: ReactNode;
}) => createElement(RouterInfoContext.Provider, { value }, children);

export const useRouterInfo = (): RouterInfo => {
  const provided = useContext(RouterInfoContext);
  // next/compat/router returns null instead of throwing outside the Pages Router
  const router = useRouter();

  if (provided) {
    return provided;
  }

  return {
    locale: router?.locale ?? "",
    route: router?.route ?? "",
    pathname: router?.pathname ?? "",
    asPath: router?.asPath ?? "",
    push: (url: string) => {
      router?.push(url);
    },
  };
};
